import type * as Highcharts from 'highcharts';
import type { PackageSolutionGroupResult } from '../../types';
import { bestValueDataLabel, type ChartBuildOptions } from './bestValueLabel';

const NPV_COLOR_STOPS: [number, string][] = [
  [0.0, '#ec2e10'],
  [0.25, '#f06d2f'],
  [0.5, '#f7b32b'],
  [0.75, '#f7e967'],
  [0.79, '#d6f77b'],
  [0.82, '#8be38b'],
  [0.85, '#4ecdc4'],
  [0.88, '#90CAF9'],
  [0.91, '#2196F3'],
  [0.94, '#1565C0'],
  [0.97, '#2b485c'],
  [1.0, '#0B132B'],
];

/** NPV compact enough for a cell of the report: thousands rounded (`292k`), millions with up to two decimals (`1.25M`). */
export const compactNpv = (value: number): string => {
  const magnitude = Math.abs(value);
  if (magnitude >= 1e6) return `${Number((value / 1e6).toFixed(2))}M`;
  if (magnitude >= 1e3) return `${Math.round(value / 1e3)}k`;
  return String(Math.round(value) || 0);
};

// Widest label (`-1.25M`) in px per px of font, and the cell labels' size limits in the report.
const EXPORT_LABEL_FONT_RANGE = { min: 8, max: 16 };
const EXPORT_LABEL_WIDTH_BUDGET = 230;

const sortedUnique = (values: number[]) => Array.from(new Set(values)).sort((a, b) => a - b);

/** NPV gain of every (cleanings, devices) combination of a solution group; the best NPV is marked. */
export const buildNpvHeatmapOptions = (
  group: PackageSolutionGroupResult,
  title: string,
  { forExport }: ChartBuildOptions = {},
): Highcharts.Options => {
  const cleanings = sortedUnique(group.solutions.map((solution) => solution.num_cleanings));
  const devices = sortedUnique(group.solutions.map((solution) => solution.num_devices));
  const maxNPV = group.solutions.reduce((max, solution) => Math.max(max, solution.net_present_value), 0);
  const best = bestValueDataLabel(forExport);
  const { min, max } = EXPORT_LABEL_FONT_RANGE;
  const labelFontPx = forExport ? Math.max(min, Math.min(max, Math.floor(EXPORT_LABEL_WIDTH_BUDGET / cleanings.length))) : 20;
  const cells = group.solutions.map((solution) => [
    cleanings.indexOf(solution?.num_cleanings),
    devices.indexOf(solution?.num_devices),
    solution?.net_present_value,
  ]);

  return {
    chart: { type: 'heatmap', marginTop: 40, marginBottom: 80, plotBorderWidth: 1 },
    title: { text: title, align: 'left' },
    // Numeric categories, as drawn on screen since the first version of the chart.
    xAxis: { title: { text: 'Number of Cleanings' }, categories: cleanings as unknown as string[] },
    yAxis: { title: { text: 'Number of Devices' }, categories: devices as unknown as string[] },
    colorAxis: { min: 0, max: maxNPV, stops: NPV_COLOR_STOPS, reversed: false },
    legend: { align: 'right', layout: 'vertical', margin: 0, verticalAlign: 'top', y: 25, symbolHeight: 280 },
    tooltip: {
      formatter: function () {
        const point = this.point as Highcharts.Point & { value: number };
        return (
          `<b>${Math.round(point.value / 10000) / 100} (M$)</b> NPV gain for ` +
          `<b>${this.series.xAxis.categories[point.x]}</b> cleanings<br>` +
          `and <b>${this.series.yAxis.categories[point.y as number]}</b> devices`
        );
      },
    },
    credits: { enabled: false },
    series: [
      {
        type: 'heatmap',
        name: 'NPV',
        borderWidth: 1,
        data: cells,
        dataLabels: {
          enabled: true,
          allowOverlap: true,
          useHTML: best.useHTML,
          formatter: function () {
            const value = (this.point as Highcharts.Point & { value: number }).value;
            if (value === maxNPV) return best.text;
            // On screen the raw value, as before; in the report a rounded one, as the cells are narrow.
            return forExport ? compactNpv(value) : value;
          },
          style: { textAlign: 'center', color: '#000', fontSize: `${labelFontPx}px` } as Highcharts.CSSObject,
        },
      },
    ],
  };
};
